import React, { useEffect, useState, useCallback } from 'react';
import { useAuth, getUserRole } from '../contexts/AuthContext';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import API_CONFIG from '../config/api';
import VisaoRestritaBanner from '../components/VisaoRestritaBanner';
import { withSetorScope, isFiscalSetorial } from '../utils/dataIsolation';
const API_BASE = `${API_CONFIG.BACKEND_URL}/api`;

// Helper para garantir URLs absolutas e tratar JSON com segurança
async function apiFetch(path, options = {}) {
  const url = path.startsWith('http') ? path : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
  const res = await fetch(url, options);
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const txt = await res.text().catch(() => '');
    if (!res.ok) throw new Error(txt || `Erro ${res.status}`);
    // tentar converter texto vazio em objeto
    return txt ? JSON.parse(txt) : {};
  }
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `Erro ${res.status}`);
  return data;
}

// Lista de setores disponíveis
const setores = [
  'Diretoria Técnica',
  'Gerência Administrativa',
  'Coordenação Operacional',
  'Coordenação de Enfermagem',
  'Financeiro',
  'Gestão de Gente',
  'Supervisão Administrativa',
  'Manutenção',
  'TI',
  'SCIH',
  'Nutrição',
  'Farmácia',
  'Comunicação',
  'Qualidade',
  'Jurídico',
];

export default function ContratosPage() {
  const { unidadeSelecionada, user, rbacRole, fiscalSetor } = useAuth();
  const visaoFiscal = isFiscalSetorial(user, rbacRole || getUserRole(user));
  const scopeOpts = { user, rbacRole: rbacRole || getUserRole(user), fiscalSetor };
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [aditivos, setAditivos] = useState({});
  
  // Estados para filtros avançados
  const [filtroAvancadoOpen, setFiltroAvancadoOpen] = useState(false);
  const [filtros, setFiltros] = useState({
    setor: '',
    tipoServico: '',
    status: '',
    dataInicioInicio: '',
    dataInicioFim: '',
    dataFimInicio: '',
    dataFimFim: '',
    valorMinimo: '',
    valorMaximo: ''
  });
  const [contratosFiltrados, setContratosFiltrados] = useState([]);

  // Estados Lista de Contatos
  const [contatosOpen, setContatosOpen] = useState(false);
  const [contatos, setContatos] = useState([]);
  const [tiposServico, setTiposServico] = useState([]);
  const [contatosLoading, setContatosLoading] = useState(false);
  const [filtroContato, setFiltroContato] = useState('');
  const [filtroTipoId, setFiltroTipoId] = useState('');

  const [formContato, setFormContato] = useState({
    id: null,
    empresa: '',
    telefone: '',
    email: '',
    objeto_servico: '',
    tipo_servico_id: ''
  });
  const [tipoNovo, setTipoNovo] = useState('');

  const fetchAditivos = useCallback(async () => {
    try {
      const urlContratos = unidadeSelecionada 
        ? `/contratos?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : `/contratos`;
      
      // Buscar todos os contratos primeiro para obter os números de contrato
      const contratos = await apiFetch(urlContratos);
      
      console.log('Contratos carregados:', contratos); // Debug
      
      // Buscar aditivos para cada contrato
      const todosAditivos = [];
      for (const contrato of contratos) {
        try {
          const aditivos = await apiFetch(`/aditivos?numeroContrato=${encodeURIComponent(contrato.numeroContrato)}`);
          todosAditivos.push(...aditivos);
        } catch (error) {
          console.error(`Erro ao buscar aditivos para contrato ${contrato.numeroContrato}:`, error);
        }
      }
      
      console.log('Todos os aditivos carregados:', todosAditivos); // Debug
      
      // Agrupar aditivos por empresa e encontrar o último de cada uma
      const aditivosPorEmpresa = {};
      todosAditivos.forEach(aditivo => {
        const empresa = aditivo.empresa || aditivo.nomeEmpresa; // Tenta ambos os campos
        if (!aditivosPorEmpresa[empresa]) {
          aditivosPorEmpresa[empresa] = [];
        }
        aditivosPorEmpresa[empresa].push(aditivo);
      });
      
      console.log('Aditivos por empresa:', aditivosPorEmpresa); // Debug
      
      // Encontrar o último aditivo de cada empresa
      const ultimosAditivos = {};
      Object.keys(aditivosPorEmpresa).forEach(empresa => {
        const aditivosEmpresa = aditivosPorEmpresa[empresa];
        const ultimoAditivo = aditivosEmpresa.sort((a, b) => 
          parseInt(b.numeroAditivo) - parseInt(a.numeroAditivo)
        )[0];
        ultimosAditivos[empresa] = ultimoAditivo;
      });
      
      console.log('Últimos aditivos:', ultimosAditivos); // Debug
      setAditivos(ultimosAditivos);
    } catch (error) {
      console.error('Erro ao buscar aditivos:', error); // Debug
      setAditivos({});
    }
  }, [unidadeSelecionada]);

  const fetchContratos = useCallback(async () => {
    setLoading(true);
    try {
      // TODO Backend: GET /api/contratos deve filtrar SQL por ?setor= (Fase 5)
      let url = unidadeSelecionada 
        ? `/contratos?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : `/contratos`;
      url = withSetorScope(url, scopeOpts);
      
      const data = await apiFetch(url);
      setContratos(data);
    } catch {
      setContratos([]);
    } finally {
      setLoading(false);
    }
  }, [unidadeSelecionada, user, rbacRole, fiscalSetor]);

  const fetchTiposServico = useCallback(async () => {
    try {
      const data = await apiFetch(`/tipos-servico?unidade=${encodeURIComponent(unidadeSelecionada?.codigo || '')}`);
      setTiposServico(data);
    } catch (e) {
      setTiposServico([]);
    }
  }, [unidadeSelecionada]);

  const fetchContatos = useCallback(async () => {
    setContatosLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroContato) params.append('search', filtroContato);
      if (filtroTipoId) params.append('tipoServicoId', filtroTipoId);
      if (unidadeSelecionada?.codigo) params.append('unidade', unidadeSelecionada.codigo);
      const data = await apiFetch(`/contatos-fornecedores?${params.toString()}`);
      setContatos(Array.isArray(data) ? data : []);
    } catch (e) {
      setContatos([]);
    } finally {
      setContatosLoading(false);
    }
  }, [filtroContato, filtroTipoId, unidadeSelecionada]);

  useEffect(() => {
    fetchContratos();
    fetchAditivos();
  }, [fetchContratos, fetchAditivos]);

  useEffect(() => {
    if (contatosOpen) {
      fetchTiposServico();
      fetchContatos();
    }
  }, [contatosOpen, fetchTiposServico, fetchContatos]);

  // Função auxiliar para detectar se é apenas ano e ajustar data
  const ajustarDataParaAno = (dataString, isFim = false) => {
    const data = new Date(dataString);
    
    // Se a string tem apenas 4 caracteres, é um ano
    if (dataString.length === 4) {
      const ano = parseInt(dataString);
      if (isFim) {
        // Para data fim, usar 31/12 do ano
        return new Date(ano, 11, 31, 23, 59, 59);
      } else {
        // Para data início, usar 01/01 do ano
        return new Date(ano, 0, 1, 0, 0, 0);
      }
    }
    
    // Se a string tem formato YYYY-MM (7 caracteres), é ano-mês
    if (dataString.length === 7 && dataString.includes('-')) {
      const [ano, mes] = dataString.split('-').map(Number);
      if (isFim) {
        // Para data fim, usar último dia do mês
        return new Date(ano, mes, 0, 23, 59, 59);
      } else {
        // Para data início, usar primeiro dia do mês
        return new Date(ano, mes - 1, 1, 0, 0, 0);
      }
    }
    
    return data;
  };

  // Recarregar aditivos quando contratos mudarem
  useEffect(() => {
    if (contratos.length > 0) {
      fetchAditivos();
    }
  }, [contratos.length, fetchAditivos]); // Usar apenas o length para evitar loops

  // Função para aplicar filtros avançados
  const aplicarFiltros = useCallback(() => {
    let resultado = [...contratos];
    
    // Debug: Mostrar dados de exemplo para verificar estrutura
    if (contratos.length > 0) {
      console.log('Exemplo de contrato:', contratos[0]);
      console.log('Campos de tipo de serviço:', {
        servicoMedico: contratos[0].servicoMedico,
        eventual: contratos[0].eventual,
        corporativo: contratos[0].corporativo,
        tipoServico: contratos[0].tipoServico,
        tipo: contratos[0].tipo
      });
    }


    // Filtro por setor
    if (filtros.setor) {
      resultado = resultado.filter(c => c.setor === filtros.setor);
    }

    // Filtro por tipo de serviço
    if (filtros.tipoServico) {
      resultado = resultado.filter(c => {
        console.log(`Filtrando por ${filtros.tipoServico}:`, {
          empresa: c.empresa,
          servicoMedico: c.servicoMedico,
          eventual: c.eventual,
          corporativo: c.corporativo,
          tipo: c.tipo,
          tipoServico: c.tipoServico
        });
        
        if (filtros.tipoServico === 'Serviço Médico') {
          // Serviço médico: campo servicoMedico = 1
          const isMedico = c.servicoMedico === 1 || c.servicoMedico === true || c.servicoMedico === '1';
          console.log(`  É médico? ${isMedico}`);
          return isMedico;
        }
        
        if (filtros.tipoServico === 'Corporativo') {
          // Corporativo: campo corporativo = 1
          const isCorporativo = c.corporativo === 1 || c.corporativo === true || c.corporativo === '1';
          console.log(`  É corporativo? ${isCorporativo}`);
          return isCorporativo;
        }
        
        if (filtros.tipoServico === 'Eventual') {
          // Eventual: campo eventual = 1
          const isEventual = c.eventual === 1 || c.eventual === true || c.eventual === '1';
          console.log(`  É eventual? ${isEventual}`);
          return isEventual;
        }
        
        if (filtros.tipoServico === 'Terceirizada') {
          // Terceirizada: NÃO é médico, NÃO é corporativo, NÃO é eventual
          const isMedico = c.servicoMedico === 1 || c.servicoMedico === true || c.servicoMedico === '1';
          const isCorporativo = c.corporativo === 1 || c.corporativo === true || c.corporativo === '1';
          const isEventual = c.eventual === 1 || c.eventual === true || c.eventual === '1';
          const isTerceirizada = !isMedico && !isCorporativo && !isEventual;
          console.log(`  É terceirizada? ${isTerceirizada} (medico:${isMedico}, corporativo:${isCorporativo}, eventual:${isEventual})`);
          return isTerceirizada;
        }
        
        return true;
      });
    }


    // Filtro por data de início
    if (filtros.dataInicioInicio || filtros.dataInicioFim) {
      resultado = resultado.filter(c => {
        if (!c.dataInicio) return false;
        
        const dataInicio = new Date(c.dataInicio);
        if (isNaN(dataInicio.getTime())) return false;

        if (filtros.dataInicioInicio) {
          const dataInicioFiltro = ajustarDataParaAno(filtros.dataInicioInicio, false);
          if (dataInicio < dataInicioFiltro) return false;
        }

        if (filtros.dataInicioFim) {
          const dataFimFiltro = ajustarDataParaAno(filtros.dataInicioFim, true);
          if (dataInicio > dataFimFiltro) return false;
        }

        return true;
      });
    }

    // Filtro por data de fim
    if (filtros.dataFimInicio || filtros.dataFimFim) {
      resultado = resultado.filter(c => {
        if (!c.dataFim) return false;
        
        const dataFim = new Date(c.dataFim);
        if (isNaN(dataFim.getTime())) return false;

        if (filtros.dataFimInicio) {
          const dataInicioFiltro = ajustarDataParaAno(filtros.dataFimInicio, false);
          if (dataFim < dataInicioFiltro) return false;
        }

        if (filtros.dataFimFim) {
          const dataFimFiltro = ajustarDataParaAno(filtros.dataFimFim, true);
          if (dataFim > dataFimFiltro) return false;
        }

        return true;
      });
    }

    // Filtro por valor
    if (filtros.valorMinimo || filtros.valorMaximo) {
      resultado = resultado.filter(c => {
        const valor = parseFloat(c.valorContrato) || 0;

        if (filtros.valorMinimo) {
          const valorMinimo = parseFloat(filtros.valorMinimo);
          if (valor < valorMinimo) return false;
        }

        if (filtros.valorMaximo) {
          const valorMaximo = parseFloat(filtros.valorMaximo);
          if (valor > valorMaximo) return false;
        }

        return true;
      });
    }

    setContratosFiltrados(resultado);
  }, [contratos, filtros]);

  // Aplicar filtros quando contratos ou filtros mudarem
  useEffect(() => {
    aplicarFiltros();
  }, [aplicarFiltros]);

  // Função para limpar filtros
  const limparFiltros = () => {
    setFiltros({
      setor: '',
      tipoServico: '',
      status: '',
      dataInicioInicio: '',
      dataInicioFim: '',
      dataFimInicio: '',
      dataFimFim: '',
      valorMinimo: '',
      valorMaximo: ''
    });
  };

  // Função para verificar se há filtros ativos
  const temFiltrosAtivos = () => {
    return Object.values(filtros).some(valor => valor !== '');
  };

  // Função para aplicar busca simples (combinada com filtros avançados)
  const contratosFinais = temFiltrosAtivos() 
    ? contratosFiltrados.filter((contrato) =>
        contrato.numeroContrato?.toLowerCase().includes(search.toLowerCase()) ||
        contrato.titulo?.toLowerCase().includes(search.toLowerCase()) ||
        contrato.empresa?.toLowerCase().includes(search.toLowerCase()) ||
        contrato.objeto?.toLowerCase().includes(search.toLowerCase())
      )
    : contratos.filter((contrato) =>
        contrato.numeroContrato?.toLowerCase().includes(search.toLowerCase()) ||
        contrato.titulo?.toLowerCase().includes(search.toLowerCase()) ||
        contrato.empresa?.toLowerCase().includes(search.toLowerCase()) ||
        contrato.objeto?.toLowerCase().includes(search.toLowerCase())
      );

  const getAditivoText = (empresa) => {
    console.log('Buscando aditivo para empresa:', empresa); // Debug
    console.log('Aditivos disponíveis:', aditivos); // Debug
    
    const ultimoAditivo = aditivos[empresa];
    if (!ultimoAditivo) {
      console.log('Nenhum aditivo encontrado para:', empresa); // Debug
      return 'Contrato Original';
    }
    
    console.log('Último aditivo encontrado:', ultimoAditivo); // Debug
    
    const numero = parseInt(ultimoAditivo.numeroAditivo);
    if (isNaN(numero)) {
      console.log('Número do aditivo inválido:', ultimoAditivo.numeroAditivo); // Debug
      return 'Contrato Original';
    }
    
    if (numero === 1) return '1° Aditivo';
    if (numero === 2) return '2° Aditivo';
    if (numero === 3) return '3° Aditivo';
    return `${numero}° Aditivo`;
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'R$ 0,00';
    return numValue.toLocaleString('pt-BR', { 
      style: 'currency', 
      currency: 'BRL' 
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Se a data está no formato DD/MM/YYYY, retorna como está
    if (dateString.includes('/') && dateString.split('/').length === 3) {
      return dateString; // Já está no formato correto
    }
    
    // Se a data está no formato YYYY-MM-DD, converte para DD/MM/YYYY
    if (dateString.includes('-') && dateString.split('-').length === 3) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Fallback para o comportamento original
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('pt-BR');
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_BASE}/contratos/export-excel`);
      if (!res.ok) throw new Error('Erro ao exportar Excel');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contratos_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSnackbar({ open: true, message: 'Exportação realizada com sucesso!', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Erro ao exportar Excel.', severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const salvarContato = async () => {
    try {
      const payload = {
        id: formContato.id || undefined,
        empresa: formContato.empresa,
        telefone: formContato.telefone || null,
        email: formContato.email || null,
        objeto: formContato.objeto_servico || null, // backend espera 'objeto'
        tipoServicoId: formContato.tipo_servico_id || null, // backend espera 'tipoServicoId'
        unidade: unidadeSelecionada?.codigo || null,
      };
      const method = formContato.id ? 'PUT' : 'POST';
      const path = formContato.id ? `/contatos-fornecedores/${formContato.id}` : `/contatos-fornecedores`;
      await apiFetch(path, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      setFormContato({ id: null, empresa: '', telefone: '', email: '', objeto_servico: '', tipo_servico_id: '' });
      fetchContatos();
      setSnackbar({ open: true, message: 'Contato salvo com sucesso!', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Erro ao salvar contato', severity: 'error' });
    }
  };

  const deletarContato = async (id) => {
    if (!window.confirm('Deseja excluir este contato?')) return;
    try {
      await apiFetch(`/contatos-fornecedores/${id}`, { method: 'DELETE' });
      fetchContatos();
      setSnackbar({ open: true, message: 'Contato excluído', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Erro ao excluir', severity: 'error' });
    }
  };

  const criarTipoServico = async () => {
    if (!tipoNovo.trim()) return;
    try {
      await apiFetch(`/tipos-servico`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome: tipoNovo.trim(), unidade: unidadeSelecionada?.codigo || null })
      });
      setTipoNovo('');
      fetchTiposServico();
      setSnackbar({ open: true, message: 'Tipo criado com sucesso!', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Erro ao criar tipo', severity: 'error' });
    }
  };

  return (
    <>
      <VisaoRestritaBanner />
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={700}>Contratos</Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setFiltroAvancadoOpen(true)}
                color={temFiltrosAtivos() ? "primary" : "inherit"}
              >
                Filtros Avançados
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setContatosOpen(true)}
              >
                Lista de Contatos
              </Button>
              <Button
                variant="outlined"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportExcel}
                disabled={exporting}
              >
                {exporting ? 'Exportando...' : 'Exportar Excel'}
              </Button>
            </Box>
          </Box>
          
          {/* Indicador de filtros ativos */}
          {temFiltrosAtivos() && (
            <Box mb={2}>
              <Chip
                label={`Filtros ativos (${contratosFinais.length} de ${contratos.length} contratos)`}
                color="primary"
                onDelete={limparFiltros}
                deleteIcon={<ClearIcon />}
                variant="outlined"
              />
            </Box>
          )}
          <TextField
            label="Buscar contrato, título, empresa ou objeto"
            variant="outlined"
            size="small"
            fullWidth
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ mb: 2 }}
          />
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nº Contrato</TableCell>
                    <TableCell>Empresa</TableCell>
                    <TableCell sx={{ 
                      maxWidth: 400, 
                      minWidth: 300,
                      fontWeight: 'bold'
                    }}>Objeto</TableCell>
                    <TableCell>Aditivo(s)</TableCell>
                    <TableCell>Valor</TableCell>
                    <TableCell>Início</TableCell>
                    <TableCell>Fim</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contratosFinais.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">Nenhum contrato encontrado</TableCell>
                    </TableRow>
                  ) : (
                    contratosFinais.map((contrato) => (
                      <TableRow key={contrato.id}>
                        <TableCell>{contrato.numeroContrato}</TableCell>
                        <TableCell>{contrato.empresa}</TableCell>
                        <TableCell sx={{ 
                          maxWidth: 400, 
                          minWidth: 300,
                          wordWrap: 'break-word',
                          fontSize: '0.875rem',
                          lineHeight: 1.4
                        }}>
                          {contrato.objeto || 'Não informado'}
                        </TableCell>
                        <TableCell>{getAditivoText(contrato.empresa)}</TableCell>
                        <TableCell>{formatCurrency(contrato.valorContrato)}</TableCell>
                        <TableCell>{formatDate(contrato.dataInicio)}</TableCell>
                        <TableCell>{formatDate(contrato.dataFim)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Modal de Filtros Avançados */}
      <Dialog 
        open={filtroAvancadoOpen} 
        onClose={() => setFiltroAvancadoOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">🔍 Filtros Avançados</Typography>
            <Button
              startIcon={<ClearIcon />}
              onClick={limparFiltros}
              color="error"
              variant="outlined"
              size="small"
            >
              Limpar Filtros
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>

            {/* Filtro por Setor */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Setor</InputLabel>
                <Select
                  value={filtros.setor}
                  onChange={(e) => setFiltros({...filtros, setor: e.target.value})}
                  label="Setor"
                >
                  <MenuItem value="">Todos os Setores</MenuItem>
                  {setores.map((setor) => (
                    <MenuItem key={setor} value={setor}>{setor}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Filtro por Tipo de Serviço */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Serviço</InputLabel>
                <Select
                  value={filtros.tipoServico}
                  onChange={(e) => setFiltros({...filtros, tipoServico: e.target.value})}
                  label="Tipo de Serviço"
                >
                  <MenuItem value="">Todos os Tipos</MenuItem>
                  <MenuItem value="Serviço Médico">Serviço Médico</MenuItem>
                  <MenuItem value="Terceirizada">Terceirizada</MenuItem>
                  <MenuItem value="Corporativo">Corporativo</MenuItem>
                  <MenuItem value="Eventual">Eventual</MenuItem>
                </Select>
              </FormControl>
            </Grid>


            {/* Filtro por Valor */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                💰 Valor do Contrato
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Valor Mínimo"
                    type="number"
                    value={filtros.valorMinimo}
                    onChange={(e) => setFiltros({...filtros, valorMinimo: e.target.value})}
                    fullWidth
                    placeholder="0.00"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Valor Máximo"
                    type="number"
                    value={filtros.valorMaximo}
                    onChange={(e) => setFiltros({...filtros, valorMaximo: e.target.value})}
                    fullWidth
                    placeholder="999999.99"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Filtro por Data de Início */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                📅 Data de Início do Contrato
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Data Inicial (ex: 2024 ou 2024-01 ou 2024-01-15)"
                    value={filtros.dataInicioInicio}
                    onChange={(e) => setFiltros({...filtros, dataInicioInicio: e.target.value})}
                    fullWidth
                    placeholder="Digite ano, ano-mês ou data completa"
                    helperText="Apenas ano (2024), ano-mês (2024-01) ou data completa"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Data Final (ex: 2024 ou 2024-12 ou 2024-12-31)"
                    value={filtros.dataInicioFim}
                    onChange={(e) => setFiltros({...filtros, dataInicioFim: e.target.value})}
                    fullWidth
                    placeholder="Digite ano, ano-mês ou data completa"
                    helperText="Apenas ano (2024), ano-mês (2024-12) ou data completa"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Filtro por Data de Fim */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                📆 Data de Fim do Contrato
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Data Inicial (ex: 2024 ou 2024-01 ou 2024-01-15)"
                    value={filtros.dataFimInicio}
                    onChange={(e) => setFiltros({...filtros, dataFimInicio: e.target.value})}
                    fullWidth
                    placeholder="Digite ano, ano-mês ou data completa"
                    helperText="Apenas ano (2024), ano-mês (2024-01) ou data completa"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Data Final (ex: 2024 ou 2024-12 ou 2024-12-31)"
                    value={filtros.dataFimFim}
                    onChange={(e) => setFiltros({...filtros, dataFimFim: e.target.value})}
                    fullWidth
                    placeholder="Digite ano, ano-mês ou data completa"
                    helperText="Apenas ano (2024), ano-mês (2024-12) ou data completa"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Resumo dos Resultados */}
            <Grid item xs={12}>
              <Box 
                sx={{ 
                  p: 2, 
                  backgroundColor: 'primary.light', 
                  borderRadius: 1,
                  textAlign: 'center'
                }}
              >
                <Typography variant="body2" color="white">
                  📊 Resultados: {contratosFinais.length} de {contratos.length} contratos encontrados
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFiltroAvancadoOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Lista de Contatos */}
      <Dialog open={contatosOpen} onClose={() => setContatosOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Lista de Contatos</DialogTitle>
        <DialogContent>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField
              label="Buscar por nome, telefone ou e-mail"
              value={filtroContato}
              onChange={(e) => setFiltroContato(e.target.value)}
              onBlur={fetchContatos}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Tipo de Serviço</InputLabel>
              <Select
                label="Tipo de Serviço"
                value={filtroTipoId}
                onChange={(e) => { setFiltroTipoId(e.target.value); }}
                onClose={fetchContatos}
              >
                <MenuItem value="">Todos</MenuItem>
                {tiposServico.map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField label="Empresa" value={formContato.empresa} onChange={(e)=>setFormContato({...formContato, empresa:e.target.value})} fullWidth />
            <TextField label="Telefone" value={formContato.telefone} onChange={(e)=>setFormContato({...formContato, telefone:e.target.value})} fullWidth />
            <TextField label="E-mail" value={formContato.email} onChange={(e)=>setFormContato({...formContato, email:e.target.value})} fullWidth />
            <TextField label="Objeto do serviço" value={formContato.objeto_servico} onChange={(e)=>setFormContato({...formContato, objeto_servico:e.target.value})} fullWidth />

            <FormControl fullWidth>
              <InputLabel>Tipo de Serviço</InputLabel>
              <Select
                label="Tipo de Serviço"
                value={formContato.tipo_servico_id}
                onChange={(e)=>setFormContato({...formContato, tipo_servico_id:e.target.value})}
              >
                {tiposServico.map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box display="flex" alignItems="center" gap={1}>
              <TextField label="Novo tipo" value={tipoNovo} onChange={(e)=>setTipoNovo(e.target.value)} fullWidth />
              <Button variant="outlined" onClick={criarTipoServico}>+</Button>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            {contatosLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={100}><CircularProgress /></Box>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Empresa</TableCell>
                      <TableCell>Telefone</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Objeto</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contatos.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center">Nenhum contato encontrado</TableCell></TableRow>
                    ) : contatos.map((c) => (
                      <TableRow key={c.id} hover>
                        <TableCell>{c.empresa}</TableCell>
                        <TableCell>{c.telefone || '-'}</TableCell>
                        <TableCell>{c.email || '-'}</TableCell>
                        <TableCell>{c.objeto || '-'}</TableCell>
                        <TableCell>{c.tipoServicoNome || '-'}</TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={()=>setFormContato({ id:c.id, empresa:c.empresa, telefone:c.telefone||'', email:c.email||'', objeto_servico:c.objeto||'', tipo_servico_id:c.tipoServicoId||'' })}>Editar</Button>
                          {!visaoFiscal && (
                          <Button size="small" color="error" onClick={()=>deletarContato(c.id)}>Excluir</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setContatosOpen(false)}>Fechar</Button>
          <Button variant="contained" onClick={salvarContato}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
} 